import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import { CONTACT_EMAIL, SOCIALS } from "../data/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field = "w-full border-b border-[var(--border)] bg-transparent py-3 text-[var(--text)] outline-none transition-colors duration-300 placeholder:text-[var(--muted)] focus:border-[var(--gold)]";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please add your name, email and a message.");
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. Let's build something.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error("Something went wrong — email me directly instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-contact" className="min-h-screen px-6 pb-32 pt-40 sm:pt-48">
      <Seo
        title="Contact — Start a Conversation | AberCal"
        description="Tell AberCal your idea and get a straight answer on how to build it. Custom platforms, websites and AI visuals for businesses in Calgary. No sales pitch, no obligation."
        path="/contact"
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 lg:grid-cols-2">
        {/* Left */}
        <div>
          <p className="mb-6 text-xs uppercase tracking-[0.3em] text-gold">Contact</p>
          <h1 className="font-head text-5xl font-light leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
            Start a <span className="text-gold italic">Conversation.</span>
          </h1>
          <p className="mt-8 max-w-md text-base leading-relaxed text-[var(--text-2)]">
            Tell me the idea — I'll tell you how to bring it to life. No sales pitch, no obligation. Just a straight conversation about what your business could run on, and whether I'm the right person to build it.
          </p>
          <div className="tartan-line mt-10 w-24" />

          <div className="mt-12 space-y-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--muted)]">Email</p>
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-mailto" className="hover-underline text-lg text-[var(--text)]">{CONTACT_EMAIL}</a>
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">[replace me — confirm address]</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--muted)]">Studio</p>
              <p className="text-lg text-[var(--text)]">Calgary, Alberta</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">[replace me — address & phone]</p>
            </div>
            <div className="flex gap-6 pt-2">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} data-testid={`social-${s.label.toLowerCase()}`}
                  className="hover-underline text-xs uppercase tracking-[0.25em] text-[var(--text-2)]">{s.label} ↗</a>
              ))}
            </div>
          </div>
        </div>

        {/* Right — form */}
        <form onSubmit={submit} className="lg:pt-16">
          <div className="space-y-8">
            <input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your name" className={field} />
            <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={field} />
            <input data-testid="contact-company" value={form.company} onChange={set("company")} placeholder="Company (optional)" className={field} />
            <textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={4} placeholder="What do you want to build?" className={`${field} resize-none`} />
          </div>
          <button data-testid="contact-submit" type="submit" disabled={sending} className="btn-gold mt-12 inline-flex items-center gap-2 disabled:opacity-60">
            {sending && <Loader2 size={15} className="animate-spin" />} Send Message
          </button>
        </form>
      </div>
    </div>
  );
}
